import * as THREE from 'three'
import { countryCoordinates } from './countries'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

class Globe {
  constructor(el) {
    this.el = el
    this.container = el.querySelector('#globe-container')
    this.markerType = this.container.dataset.markerType
    this.markers = JSON.parse(this.container.dataset.markers)
    this.countryCoordinates = countryCoordinates
    this.zoomIn = this.container.querySelector('.globe__zoom-in')
    this.zoomOut = this.container.querySelector('.globe__zoom-out')
    this.numberMarkers = []
    this.logos = []
    this.showContinentLabels = this.container.dataset.continentLabels === 'true'

    this.continentCoordinates = [
      { name: 'Europe', lat: 54, lng: 15 },
      { name: 'Middle\nEast', lat: 29, lng: 45 },
      { name: 'Africa', lat: 1, lng: 17 },
      { name: 'North\nAmerica', lat: 35.0, lng: -100.0 },
      { name: 'South\nAmerica', lat: -15.0, lng: -60.0 },
      { name: 'South East\nAsia', lat: 15, lng: 105 },
      { name: 'China', lat: 30, lng: 100 },
      { name: 'Japan', lat: 30, lng: 138 },
      { name: 'India', lat: 18, lng: 78 },
      { name: 'Oceania', lat: -25, lng: 135 }
    ]

    this.init()
  }

  init() {
    // Create scene
    this.scene = new THREE.Scene()

    // Create camera
    this.camera = new THREE.PerspectiveCamera(75, this.container.clientWidth / this.container.clientHeight, 0.1, 1000)
    this.camera.position.z = 3.5
    this.camera.position.y = 1.5

    // Create renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.container.appendChild(this.renderer.domElement)

    // Load SVG texture
    const textureLoader = new THREE.TextureLoader()
    textureLoader.load('./dist/images/dotted-world-map.png', (texture) => {
      const geometry = new THREE.SphereGeometry(2, 64, 64)
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
      })
      this.globe = new THREE.Mesh(geometry, material)
      this.scene.add(this.globe)

      if (this.markerType === 'logos') {
        this.addLogos()
      } else if (this.markerType === 'numbers') {
        this.addNumbers()
      }

      if (this.showContinentLabels) {
        this.addContinentLabels()
      }
    })

    if (this.zoomIn) this.zoomIn.addEventListener('click', () => this.zoomCamera(true))
    if (this.zoomOut) this.zoomOut.addEventListener('click', () => this.zoomCamera(false))

    // Initialize OrbitControls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.05
    this.controls.rotateSpeed = 0.5
    this.controls.enableZoom = false
    this.controls.enablePan = false
    this.controls.minPolarAngle = Math.PI / 3
    this.controls.maxPolarAngle = (Math.PI * 2) / 3
    this.controls.autoRotate = true
    this.controls.autoRotateSpeed = 0.5

    // Handle resize
    window.addEventListener('resize', this.onWindowResize.bind(this))

    // Start animation
    this.animate()
  }

  latLngToVector3(lat, lng, radius) {
    const phi = ((90 - lat) * Math.PI) / 180
    const theta = ((lng + 180) * Math.PI) / 180

    const x = -radius * Math.sin(phi) * Math.cos(theta)
    const y = radius * Math.cos(phi)
    const z = radius * Math.sin(phi) * Math.sin(theta)

    return new THREE.Vector3(x, y, z)
  }

  // Helper to calculate distance between two lat/lng points
  distanceBetweenPoints(lat1, lng1, lat2, lng2) {
    // Using the Haversine formula
    const R = 6371 // Earth's radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180
    const dLng = ((lng2 - lng1) * Math.PI) / 180
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  // Group nearby logos
  groupNearbyLogos(logos, threshold = 10) {
    // threshold in km
    const groups = []

    logos.forEach((logo) => {
      const position = logo.position
      let foundGroup = false

      for (const group of groups) {
        if (
          this.distanceBetweenPoints(
            position.lat,
            position.lng,
            group[0].position.lat,
            group[0].position.lng
          ) < threshold
        ) {
          group.push(logo)
          foundGroup = true
          break
        }
      }

      if (!foundGroup) {
        groups.push([logo])
      }
    })

    return groups
  }

  addLogos() {
    const coordinatesMap = this.countryCoordinates.reduce((map, country) => {
      map[country.name] = {
        lat: country.latitude,
        lng: country.longitude,
      }
      return map
    }, {})

    const logoData = this.markers.filter((marker) => {
      const hasCoordinates = coordinatesMap.hasOwnProperty(marker.country)
      if (!hasCoordinates) {
        console.warn(`No coordinates found for ${marker.country}`)
      }
      return hasCoordinates
    }).map((marker) => ({
      name: marker.airline,
      url: marker.logo,
      position: coordinatesMap[marker.country],
    }))

    // Group logos by proximity
    const logoGroups = this.groupNearbyLogos(logoData)

    logoGroups.forEach((group) => {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      canvas.width = 200
      canvas.height = 200

      // Use the first logo's position for the group
      const primaryLogo = group[0]
      const isGroup = group.length > 1

      const img = new Image()
      img.crossOrigin = 'anonymous'

      img.onload = () => {
        // Fill background
        context.fillStyle = '#FFDED0'
        context.beginPath()
        context.roundRect(20, 20, canvas.width - 40, canvas.height - 40, 6)
        context.fill()

        // Draw the primary logo
        const scale = Math.min(
          (canvas.width - 60) / img.width,
          (canvas.height - 60) / img.height
        )
        const centerX = (canvas.width - img.width * scale) / 2
        const centerY = (canvas.height - img.height * scale) / 2

        context.drawImage(
          img,
          centerX,
          centerY,
          img.width * scale,
          img.height * scale
        )

        // If it's a group, add the counter badge
        if (isGroup) {
          const badgeSize = 40
          const badgeX = canvas.width - 40
          const badgeY = 0

          // Draw badge circle
          context.fillStyle = '#FF7140'
          context.beginPath()
          context.roundRect(badgeX, badgeY, badgeSize, badgeSize, 6)
          context.fill()

          // Draw counter text
          context.fillStyle = 'white'
          context.font = 'bold 24px Arial'
          context.textAlign = 'center'
          context.textBaseline = 'middle'
          context.fillText(
            `+${group.length - 1}`,
            badgeX + badgeSize / 2,
            badgeY + badgeSize / 2
          )
        }

        // Create and position the sprite
        const texture = new THREE.CanvasTexture(canvas)
        texture.colorSpace = THREE.SRGBColorSpace
        texture.minFilter = THREE.LinearFilter
        texture.magFilter = THREE.LinearFilter

        const material = new THREE.SpriteMaterial({
          map: texture,
          transparent: true,
        })
        const sprite = new THREE.Sprite(material)

        // Position the logo
        const position = this.latLngToVector3(
          primaryLogo.position.lat,
          primaryLogo.position.lng,
          2.1
        )
        sprite.position.set(position.x, position.y, position.z)

        // Scale the sprite
        // sprite.scale.set(0.25, 0.25, 1)
        sprite.scale.set(0.2, 0.2, 1)

        // Store group data for zoom handling
        sprite.userData = {
          isGroup,
          groupMembers: group,
          logoName: primaryLogo.name,
        }

        this.globe.add(sprite)
        this.logos.push(sprite)

        // Add bounce animation
        if (isGroup) {
          this.addBounceAnimation(sprite)
        }
      }

      img.src = primaryLogo.url
    })
  }

  addNumbers() {
    const coordinatesMap = this.countryCoordinates.reduce((map, country) => {
      map[country.name] = {
        lat: country.latitude,
        lng: country.longitude,
      }
      return map
    }, {})

    const validMarkers = this.markers.filter((marker) => {
      const hasCoordinates = coordinatesMap.hasOwnProperty(marker.country)
      if (!hasCoordinates) {
        console.warn(`No coordinates found for ${marker.country}`)
      }
      return hasCoordinates
    })

    validMarkers.forEach((marker, index) => {
      const sprite = this.createNumberMarker(marker.number, marker.name, coordinatesMap[marker.country], false)
      this.numberMarkers.push({ sprite, number: marker.number, name: marker.name, position: coordinatesMap[marker.country] })
    })

    document.querySelectorAll('.teams__item').forEach((item, index) => {
      item.addEventListener('mouseenter', () => {
        const number = item.querySelector('.teams__item-number').textContent.trim()
        const marker = this.numberMarkers.find(m => m.number === number)
        if (marker) {
          console.log(marker)
          this.rotateToMarker(marker.position)
          this.updateMarkerStyle(marker.sprite, marker.number, marker.name, true)
        }
      })

      item.addEventListener('mouseleave', () => {
        const number = item.querySelector('.teams__item-number').textContent.trim()
        const name = item.querySelector('.teams__item-title').textContent.trim()
        const marker = this.numberMarkers.find(m => m.number === number)
        if (marker) {
          this.updateMarkerStyle(marker.sprite, marker.number, marker.name, false)
        }
      })
    })
  }

  createNumberMarker(number, name, coordinates, isHighlighted) {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    // Split name into lines (one word per line)
    const lines = name.split(' ')

    // Font and padding settings
    const fontSize = 50
    const lineHeight = fontSize * 1.2
    const paddingX = 40
    const paddingY = 40

    // Set font before measuring text
    context.font = `${fontSize}px RR Pioneer`

    // Measure width of the longest line
    const maxLineWidth = Math.max(...lines.map(line => context.measureText(line).width))

    // Calculate canvas dimensions
    const canvasWidth = Math.ceil(maxLineWidth + paddingX)
    const canvasHeight = Math.ceil(lines.length * lineHeight + paddingY)

    // Set canvas size
    canvas.width = canvasWidth
    canvas.height = canvasHeight

    // Reset font and alignment after resizing canvas
    context.font = `${fontSize}px RR Pioneer`
    context.textAlign = 'center'
    context.textBaseline = 'middle'

    // Draw background
    context.fillStyle = isHighlighted ? '#D83F11' : '#FFDED0'
    context.beginPath()
    context.roundRect(0, 0, canvasWidth, canvasHeight, 20)
    context.fill()

    // Draw text
    context.fillStyle = isHighlighted ? '#FFFFFF' : '#000000'
    const centerX = canvasWidth / 2
    const startY = (canvasHeight - (lines.length * lineHeight)) / 2 + lineHeight / 2
    lines.forEach((line, i) => {
      context.fillText(line, centerX, startY + i * lineHeight)
    })

    // Create texture
    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter

    // Create sprite
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
    })
    const sprite = new THREE.Sprite(material)

    const position = this.latLngToVector3(coordinates.lat, coordinates.lng, 2.05)
    sprite.position.set(position.x, position.y, position.z)

    // Calculate proportional sprite scale so font size stays consistent
    const baseLineHeight = 0.07 // world units per text line
    const spriteHeight = baseLineHeight * lines.length
    const spriteWidth = spriteHeight * (canvasWidth / canvasHeight)
    sprite.scale.set(spriteWidth, spriteHeight, 1)

    this.globe.add(sprite)
    return sprite
  }

  updateMarkerStyle(sprite, number, name, isHighlighted) {
    // Split name into lines (one word per line)
    const lines = name.split(' ')

    // Font and padding settings
    const fontSize = 50
    const lineHeight = fontSize * 1.2
    const paddingX = 40
    const paddingY = 40

    // Create a temporary context for measurement
    const tempCanvas = document.createElement('canvas')
    const tempContext = tempCanvas.getContext('2d')
    tempContext.font = `${fontSize}px RR Pioneer`

    // Measure text
    const maxLineWidth = Math.max(...lines.map(line => tempContext.measureText(line).width))
    const canvasWidth = Math.ceil(maxLineWidth + paddingX)
    const canvasHeight = Math.ceil(lines.length * lineHeight + paddingY)

    // Resize original canvas
    const canvas = sprite.material.map.image
    canvas.width = canvasWidth
    canvas.height = canvasHeight

    // Get updated context
    const context = canvas.getContext('2d')
    context.font = `${fontSize}px RR Pioneer`
    context.textAlign = 'center'
    context.textBaseline = 'middle'

    // Redraw background
    context.fillStyle = isHighlighted ? '#D83F11' : '#FFDED0'
    context.beginPath()
    context.roundRect(0, 0, canvasWidth, canvasHeight, 20)
    context.fill()

    // Redraw text
    context.fillStyle = isHighlighted ? '#FFFFFF' : '#000000'
    const centerX = canvasWidth / 2
    const startY = (canvasHeight - (lines.length * lineHeight)) / 2 + lineHeight / 2
    lines.forEach((line, i) => {
      context.fillText(line, centerX, startY + i * lineHeight)
    })

    // Update texture
    sprite.material.map.needsUpdate = true

    // Update sprite scale (so font size remains visually consistent)
    const baseLineHeight = 0.07
    const spriteHeight = baseLineHeight * lines.length
    const spriteWidth = spriteHeight * (canvasWidth / canvasHeight)
    sprite.scale.set(spriteWidth, spriteHeight, 1)
  }

  rotateToMarker(position) {
    // Disable auto-rotation while focusing on a marker
    this.controls.autoRotate = false

    // Calculate the target position
    const targetPosition = this.latLngToVector3(position.lat, position.lng, 2.05)

    // Calculate the camera position
    const distance = 3.5 // Keep the same distance as initial camera position
    const cameraPosition = targetPosition.clone().normalize().multiplyScalar(distance)

    // Animate the camera movement
    const duration = 1000 // 1 second
    const startPosition = this.camera.position.clone()
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Use easing function for smooth animation
      const easeProgress = 1 - Math.pow(1 - progress, 3)

      this.camera.position.lerpVectors(startPosition, cameraPosition, easeProgress)
      this.camera.lookAt(this.scene.position)

      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        // Re-enable auto-rotation after animation
        this.controls.autoRotate = true
      }
    }

    animate()
  }

  // Add bounce animation
  addBounceAnimation(sprite) {
    const originalScale = sprite.scale.clone()
    const bounceScale = originalScale.clone().multiplyScalar(1.2)
    const duration = 1000 // ms
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = elapsed / duration

      if (progress <= 1) {
        const scale = Math.sin(progress * Math.PI) * 0.2 + 1
        sprite.scale.copy(originalScale).multiplyScalar(scale)
        requestAnimationFrame(animate)
      } else {
        sprite.scale.copy(originalScale)
      }
    }

    animate()
  }

  addContinentLabels() {
    this.continentCoordinates.forEach(continent => {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      canvas.width = 256
      canvas.height = 64

      // Styling
      context.fillStyle = '#555555'
      context.font = 'bold 28px Arial'
      context.textAlign = 'center'
      context.textBaseline = 'middle'

      // Support line breaks using \n
      const lines = continent.name.split('\n')
      const lineHeight = 30
      const totalHeight = lines.length * lineHeight
      const centerY = canvas.height / 2 - totalHeight / 2 + lineHeight / 2

      lines.forEach((line, index) => {
        context.fillText(line, canvas.width / 2, centerY + index * lineHeight)
      })

      const texture = new THREE.CanvasTexture(canvas)
      texture.colorSpace = THREE.SRGBColorSpace
      texture.minFilter = THREE.LinearFilter
      texture.magFilter = THREE.LinearFilter

      const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
      })

      const sprite = new THREE.Sprite(material)
      const position = this.latLngToVector3(continent.lat, continent.lng, 2.05)
      sprite.position.copy(position)
      sprite.scale.set(0.5, 0.125, 1) // Adjust size as needed

      this.globe.add(sprite)
    })
  }

  onWindowResize() {
    this.camera.aspect = this.container.clientWidth / this.container.clientHeight
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(
      this.container.clientWidth,
      this.container.clientHeight
    )
  }

  animate() {
    requestAnimationFrame(this.animate.bind(this))

    // Update controls
    this.controls.update()
    this.renderer.render(this.scene, this.camera)
  }

  zoomCamera(zoomIn) {
    const startZ = this.camera.position.z
    const targetZ = startZ + (zoomIn ? -0.3 : 0.3)
    const duration = 500 // 500ms for the animation
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Use cubic easing for smooth animation
      const easeProgress = 1 - Math.pow(1 - progress, 3)

      // Interpolate between start and target Z positions
      this.camera.position.z = startZ + (targetZ - startZ) * easeProgress

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    animate()
  }
}

export const initGlobe = () => {
  document.querySelectorAll('.globe').forEach((el) => {
    new Globe(el)
  })
}

export default () => { }
